import { EditorState } from 'lexical';
import { ListStore } from '@list-store';
import { action, computed, makeObservable, observable } from 'mobx';
import {
    AdditionalInfoType,
    AdditionalInfoView,
    TempAdditionalInfoViewWithPinFields,
} from '../../types';
import { compareInfos, sortDirectives } from '../../helpers/sort-helpers';
import { SortDirection, UnreadInfos, UnreadInfo } from './types';
import { NarrativePagination } from '@mark43/cad-api';

export interface AdditionalInfoStoreConfig {
    sortDirection?: SortDirection;
    filter?: AdditionalInfoType[];
    firstUnreadInfo?: UnreadInfo;
    onAllRead?: () => void;
}

const { USER, SYSTEM } = AdditionalInfoType;

interface Data {
    filter?: AdditionalInfoType[];
    sortDirection?: SortDirection;
    unreads: UnreadInfo[];
    agencyEventNumberToIdMap: Record<string, number>;
    isPageReloading: boolean;
    pageFetchFailed: boolean;
    pagination: NarrativePagination;
    isFetchingNextPage: boolean;
    initialSocketConnectionMade: boolean;
    editorState?: EditorState;
    isHighPriority: boolean;
    pinnedEntries: TempAdditionalInfoViewWithPinFields[];
    isPinnedSectionExpanded: boolean;
}

const removeInvalidInitialUnreads = (info: any): boolean => {
    if (!info || typeof info !== 'object') return false;
    if (!Number.isInteger(info.id) || info.id <= 0) return false;
    if (info.priority !== 'STANDARD_UPDATE' && info.priority !== 'HIGH_PRIORITY_UPDATE')
        return false;
    return true;
};

export class AdditionalInfoStore {
    public readonly listStore: ListStore<AdditionalInfoView> = new ListStore<AdditionalInfoView>();

    public data: Data = {
        filter: undefined,
        sortDirection: undefined,
        unreads: [],
        agencyEventNumberToIdMap: {},
        isPageReloading: false,
        pageFetchFailed: false,
        pagination: { hasNextPage: true },
        isFetchingNextPage: false,
        initialSocketConnectionMade: false,
        editorState: undefined,
        isHighPriority: false,
        pinnedEntries: [],
        isPinnedSectionExpanded: false,
    };

    onAllRead?: () => void;

    constructor(config: AdditionalInfoStoreConfig) {
        this.onAllRead = config.onAllRead;
        this.setSortDirection(config.sortDirection ?? SortDirection.DESC);
        this.data.filter = config.filter ?? [USER, SYSTEM];
        this.data.unreads = config.firstUnreadInfo
            ? [config.firstUnreadInfo].filter((info) => removeInvalidInitialUnreads(info))
            : [];
        this.data.unreads.sort(compareInfos);
        makeObservable(this, {
            // observables
            data: observable,
            // computeds
            agencyEventNumberToIdMap: computed,
            earliestUnreadInfo: computed,
            editorState: computed,
            filter: computed,
            hasNextPage: computed,
            hasUnread: computed,
            hiddenPinnedCount: computed,
            initialSocketConnectionMade: computed,
            isFetchingNextPage: computed,
            isHighPriority: computed,
            isPinnedSectionExpanded: computed,
            isPageReloading: computed,
            nextPageCursor: computed,
            pageFetchFailed: computed,
            pinnedEntries: computed,
            showPinnedDivider: computed,
            sortDirection: computed,
            unreadInfos: computed,
            visiblePinnedEntries: computed,
            // actions
            clearUnreadsBasedOnOldestUnread: action,
            markAsRead: action,
            markAsUnread: action,
            removeInfos: action,
            reset: action,
            setFilter: action,
            setHasNextPage: action,
            setPinnedEntries: action,
            setInitialSocketConnectionMade: action,
            setIsFetchingNextPage: action,
            setIsHighPriority: action,
            setNextPageCursor: action,
            setPagination: action,
            setPageFetchFailed: action,
            setSortDirection: action,
            storeEditorState: action,
            toggleIsPageReloading: action,
            togglePinnedSection: action,
            upsertAgencyEventNumberToIdMap: action,
            upsertInfos: action,
        });
    }

    get isHighPriority(): boolean {
        return this.data.isHighPriority;
    }

    get editorState(): EditorState | undefined {
        return this.data.editorState;
    }

    get initialSocketConnectionMade(): boolean {
        return this.data.initialSocketConnectionMade;
    }

    get isFetchingNextPage(): boolean {
        return this.data.isFetchingNextPage;
    }

    get nextPageCursor(): number | undefined {
        return this.data.pagination.nextCursor || undefined;
    }

    get hasNextPage(): boolean {
        return this.data.pagination.hasNextPage;
    }

    get pageFetchFailed(): boolean {
        return this.data.pageFetchFailed;
    }

    get filter(): AdditionalInfoType[] | undefined {
        return this.data.filter;
    }

    get isPageReloading(): boolean {
        return this.data.isPageReloading;
    }

    get sortDirection(): SortDirection | undefined {
        return this.data.sortDirection;
    }

    get earliestUnreadInfo(): { id: number } | null {
        return this.data.unreads.length > 0 ? this.data.unreads[0] : null;
    }

    get unreadInfos(): UnreadInfos {
        return {
            STANDARD_UPDATE:
                this.data.unreads.find((info) => info.priority === 'STANDARD_UPDATE') ?? null,
            HIGH_PRIORITY_UPDATE:
                this.data.unreads.find((info) => info.priority === 'HIGH_PRIORITY_UPDATE') ?? null,
        };
    }

    get hasUnread(): boolean {
        return this.data.unreads.length !== 0;
    }

    get agencyEventNumberToIdMap(): Record<string, number> {
        return this.data.agencyEventNumberToIdMap;
    }

    setIsHighPriority = (value: boolean): void => {
        this.data.isHighPriority = value;
    };

    storeEditorState = (value: EditorState): void => {
        this.data.editorState = value;
    };

    setInitialSocketConnectionMade = (value: boolean): void => {
        this.data.initialSocketConnectionMade = value;
    };

    setIsFetchingNextPage = (value: boolean): void => {
        this.data.isFetchingNextPage = value;
    };

    setNextPageCursor = (value: number): void => {
        this.data.pagination = { ...this.data.pagination, nextCursor: value };
    };

    setHasNextPage = (value: boolean): void => {
        this.data.pagination = { ...this.data.pagination, hasNextPage: value };
    };

    setPagination = (value: NarrativePagination): void => {
        this.data.pagination = { ...this.data.pagination, ...value };
    };

    setPageFetchFailed = (value: boolean): void => {
        this.data.pageFetchFailed = value;
    };

    toggleIsPageReloading = (): void => {
        this.data.isPageReloading = !this.data.isPageReloading;
    };

    upsertAgencyEventNumberToIdMap = (agencyEventNumberToIdMap: Record<string, number>): void => {
        for (const agencyEventNumber in agencyEventNumberToIdMap) {
            this.data.agencyEventNumberToIdMap[agencyEventNumber] =
                agencyEventNumberToIdMap[agencyEventNumber];
        }
    };

    setSortDirection = (sort: SortDirection): void => {
        this.data.sortDirection = sort;
        this.listStore.setSortingDirective(sortDirectives[sort]);
    };

    setFilter = (filters: AdditionalInfoType[]): void => {
        this.data.filter = filters;
    };

    reset = (config: AdditionalInfoStoreConfig = {}): void => {
        if (config.sortDirection) {
            this.setSortDirection(config.sortDirection);
        } else if (this.data.sortDirection !== undefined) {
            this.setSortDirection(this.data.sortDirection);
        }
        this.data.filter = config.filter ?? this.data.filter;
        this.data.unreads = config.firstUnreadInfo
            ? [config.firstUnreadInfo].filter((info) => removeInvalidInitialUnreads(info))
            : this.data.unreads;
        this.data.unreads.sort(compareInfos);
        this.listStore.setItems([]);
    };

    upsertInfos = (rows: AdditionalInfoView[], markUnread = false): void => {
        if (markUnread) {
            const nonSystemInfoRows = rows.filter((row) => row.type !== AdditionalInfoType.SYSTEM);
            this.markAsUnread(nonSystemInfoRows as UnreadInfo[]);
        }
        this.listStore.upsertItems(rows);
    };

    removeInfos = (ids: number[]): void => {
        this.listStore.removeItems(ids);
        this.markAsRead(ids.map((id) => ({ id })));
    };

    markAsUnread = (unreadInfos: UnreadInfo[]): void => {
        this.data.unreads.push(...unreadInfos);
        this.data.unreads.sort(compareInfos);
    };

    // This function is used when the user scrolls to the oldest unread additional info, and the
    // requirement is that all infos become unread.
    clearUnreadsBasedOnOldestUnread = (readInfos: { id: number }[]): void => {
        if (!Array.isArray(readInfos) || readInfos.length === 0 || this.data.unreads.length === 0)
            return;
        const readIds = readInfos.map((info) => info.id);
        if (readIds.includes(this.data.unreads[0].id)) {
            this.data.unreads = [];
            this.onAllRead?.();
        }
    };

    // This function is used when an additional info is deleted due to a websocket message, and the
    // specific info should become read, but the other infos must remain unread.
    markAsRead = (readInfos: { id: number }[]): void => {
        if (!Array.isArray(readInfos) || readInfos.length === 0 || this.data.unreads.length === 0)
            return;
        const readIds = readInfos.map((info) => info.id);
        this.data.unreads = this.data.unreads.filter(({ id }) => !readIds.includes(id));
        if (this.data.unreads.length === 0) {
            this.onAllRead?.();
        }
    };

    private readonly defaultVisibleCount = 2;

    get pinnedEntries(): TempAdditionalInfoViewWithPinFields[] {
        return this.data.pinnedEntries;
    }

    get isPinnedSectionExpanded(): boolean {
        return this.data.isPinnedSectionExpanded;
    }

    get visiblePinnedEntries(): TempAdditionalInfoViewWithPinFields[] {
        if (this.isPinnedSectionExpanded || this.pinnedEntries.length <= this.defaultVisibleCount) {
            return this.pinnedEntries;
        }
        return this.pinnedEntries.slice(0, this.defaultVisibleCount);
    }

    get showPinnedDivider(): boolean {
        return this.pinnedEntries.length > this.defaultVisibleCount;
    }

    get hiddenPinnedCount(): number {
        return Math.max(0, this.pinnedEntries.length - this.defaultVisibleCount);
    }

    setPinnedEntries = (entries: TempAdditionalInfoViewWithPinFields[]): void => {
        this.data.pinnedEntries = entries;
    };

    togglePinnedSection = (): void => {
        this.data.isPinnedSectionExpanded = !this.data.isPinnedSectionExpanded;
    };
}
